import { formatDecimal, formatDurationHM } from "../../utils";
import { InformationHoverIcon } from "./InformationHoverIcon";

export type AdvisorPerformanceRow = {
  employeeName: string;
  assigned: number;
  resolved: number;
  open: number;
  avgResolutionSeconds: number | null;
  successCloseRate: number | null;
};

type Props = {
  title?: string;
  subtitle?: string;
  info?: string;
  colSpan?: number;
  rows: AdvisorPerformanceRow[];
};

const formatResolutionTime = (seconds: number | null): string =>
  seconds === null ? "—" : formatDurationHM(seconds);

const formatCloseRate = (rate: number | null): string =>
  rate === null ? "—" : `${formatDecimal(rate, 0)}%`;

const AdvisorPerformanceTable = ({
  title = "Desempeño por asesor",
  subtitle = "Solicitudes asignadas y resueltas por cada asesor en el período",
  info,
  colSpan = 1,
  rows,
}: Props) => {
  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
            {info && <InformationHoverIcon information={info} />}
          </div>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sin datos en este período
          </p>
        ) : (
          <>
            <div className="hidden md:block">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-4 border-b border-border pb-3 text-sm font-medium text-muted-foreground">
                <span>Asesor</span>
                <span className="text-right">Asignados</span>
                <span className="text-right">Resueltos</span>
                <span className="text-right">Abiertos</span>
                <span className="text-right">T. medio resol.</span>
                <span className="text-right">% cierre exitoso</span>
              </div>
              {rows.map((row) => (
                <div
                  key={row.employeeName}
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] items-center gap-4 border-b border-border py-3 text-sm last:border-b-0"
                >
                  <span className="font-medium text-palomma-secondary">{row.employeeName}</span>
                  <span className="text-right">{row.assigned}</span>
                  <span className="text-right">{row.resolved}</span>
                  <span className="text-right">{row.open}</span>
                  <span className="text-right">
                    {formatResolutionTime(row.avgResolutionSeconds)}
                  </span>
                  <span className="text-right">{formatCloseRate(row.successCloseRate)}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4 md:hidden">
              {rows.map((row) => (
                <div
                  key={row.employeeName}
                  className="flex flex-col gap-2 rounded-lg border border-border p-4"
                >
                  <p className="font-medium text-palomma-secondary">{row.employeeName}</p>
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Asignados</span>
                      <span>{row.assigned}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resueltos</span>
                      <span>{row.resolved}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Abiertos</span>
                      <span>{row.open}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">T. medio resol.</span>
                      <span>{formatResolutionTime(row.avgResolutionSeconds)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">% cierre exitoso</span>
                      <span>{formatCloseRate(row.successCloseRate)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdvisorPerformanceTable;
